import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../contexts/AuthContext';
import {
  Search, User, FileText, Calendar, Loader2, ChevronRight, Sparkles
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DossiersPage() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [dossiers, setDossiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchDossiers();
  }, [searchQuery]);

  const fetchDossiers = async () => {
    try {
      const params = {};
      if (searchQuery) params.search = searchQuery;
      const res = await axios.get(`${API}/dossiers`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });
      setDossiers(res.data);
    } catch {
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  return (
    <DashboardLayout title="Dossiers">
      <div className="space-y-4">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <p className="text-muted-foreground text-sm">
            Vos dossiers sont crees automatiquement a partir des echanges avec vos contacts
          </p>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Rechercher..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="pl-9 w-56"
              data-testid="search-dossiers"
            />
          </div>
        </div>

        {/* List */}
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        ) : dossiers.length === 0 ? (
          <Card>
            <CardContent className="py-16 text-center space-y-3">
              <div className="w-14 h-14 mx-auto rounded-full bg-muted/50 flex items-center justify-center">
                <FileText className="w-7 h-7 text-muted-foreground" />
              </div>
              <p className="text-muted-foreground">
                {searchQuery
                  ? 'Aucun dossier ne correspond a votre recherche'
                  : 'Aucun dossier pour le moment. Les dossiers seront crees automatiquement lors des prochains echanges.'}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-3" data-testid="dossiers-list">
            {dossiers.map(d => (
              <Card
                key={d.id}
                className="group cursor-pointer hover:shadow-md hover:border-primary/20 transition-all"
                onClick={() => navigate(`/dossiers/${d.id}`)}
                data-testid={`dossier-card-${d.id}`}
              >
                <CardContent className="py-4">
                  <div className="flex items-center gap-4">
                    {/* Left: info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="font-medium text-sm truncate">{d.title}</h3>
                        {d.auto_created && (
                          <Badge variant="outline" className="text-[10px] bg-sky-500/10 text-sky-600 border-sky-500/20 shrink-0">
                            Auto
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-4 mt-1.5 text-xs text-muted-foreground flex-wrap">
                        {d.primary_contact_name && (
                          <span className="flex items-center gap-1">
                            <User className="w-3 h-3" /> {d.primary_contact_name}
                          </span>
                        )}
                        <span className="flex items-center gap-1">
                          <FileText className="w-3 h-3" /> {d.memory_count} interactions
                        </span>
                        {d.last_activity && (
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            Dernier echange : {new Date(d.last_activity).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Right: arrow */}
                    <ChevronRight className="w-5 h-5 text-muted-foreground/40 group-hover:text-primary transition-colors shrink-0" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
